"use client";

import { Placeholder } from "@lmn/design-system";
import React from "react";
import styled from "styled-components";
import media from "@/styles/mediaQueries";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-top: 3rem;
`;

const SkeletonCard = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column" as const,
  borderRadius: theme.kallisto.border.radius.radius100,
  overflow: "hidden",
  border: `1px solid ${theme.kallisto.color.border.static.borderNeutral}`,
  backgroundColor: theme.kallisto.color.background.static.backgroundPage,
}));

const SkeletonHeader = styled.div(({ theme }) => ({
  backgroundColor: theme.kallisto.color.background.static.backgroundLayer01,
  borderBottom: `1px solid ${theme.kallisto.color.border.static.borderNeutral}`,
  padding: theme.kallisto.space.space100,
  display: "flex",
  alignItems: "center",
  gap: "8px",
}));

const SkeletonBody = styled.div<{ $isHotelOnly?: boolean }>(
  ({ $isHotelOnly }) => ({
    padding: "1rem",
    display: "grid",
    gap: "1rem",
    alignItems: "stretch",
    gridTemplateAreas: $isHotelOnly
      ? `"details" "price"`
      : `"details" "flights" "price"`,
    gridTemplateColumns: "1fr",
    ...media.screenSmMin({
      gridTemplateAreas: $isHotelOnly
        ? `"details price"`
        : `"details flights" "price price"`,
      gridTemplateColumns: $isHotelOnly ? "1fr 1fr" : "1fr 1fr",
    }),
    ...media.screenMdMin({
      gridTemplateAreas: $isHotelOnly
        ? `"details price"`
        : `"details flights price"`,
      gridTemplateColumns: $isHotelOnly ? "2fr 1fr" : "1fr 1fr 1fr",
    }),
  })
);

const DetailsSection = styled.div({
  gridArea: "details",
  display: "flex",
  flexDirection: "column" as const,
});

const FlightsSection = styled.div({
  gridArea: "flights",
  display: "flex",
  flexDirection: "column" as const,
});

const PriceSection = styled.div({
  gridArea: "price",
  display: "flex",
  flexDirection: "column" as const,
  gap: "0.5rem",
});

const ContentBox = styled.div(({ theme }) => ({
  border: `1px solid ${theme.kallisto.color.border.static.borderDisabled}`,
  padding: theme.kallisto.space.space150,
  borderRadius: theme.kallisto.border.radius.radius100,
  flex: 1,
  display: "flex",
  flexDirection: "column" as const,
  gap: "8px",
}));

const ServiceItem = styled.div({
  display: "flex",
  alignItems: "center",
  gap: "4px",
});

const FlightRow = styled.div({
  display: "flex",
  flexDirection: "column" as const,
  gap: "4px",
  "&:nth-child(2)": {
    marginTop: "1.5rem",
  },
});

const Separator = styled.div(({ theme }) => ({
  width: "100%",
  height: "1px",
  backgroundColor: theme.kallisto.color.border.static.borderNeutral,
  margin: "0.8rem 0",
}));

const PriceRow = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  gap: "1rem",
  marginTop: "auto",
});

const TourOperatorLogoContainer = styled.div(({ theme }) => ({
  marginLeft: "auto",
  backgroundColor: theme.kallisto.color.background.static.backgroundLayer01,
  padding: "4px",
  borderRadius: theme.kallisto.border.radius.radius50,
}));

const TitlePlaceholder = styled.div({
  marginBottom: "12px",
});

const PriceContainer = styled.div({
  display: "flex",
  flexDirection: "column" as const,
  gap: "4px",
});

interface OfferListItemSkeletonProps {
  isHotelOnly?: boolean;
}

export const OfferListItemSkeleton: React.FC<OfferListItemSkeletonProps> = ({
  isHotelOnly = false,
}) => (
  <SkeletonCard>
    <SkeletonHeader>
      <Placeholder width="180px" height="20px" borderRadius="radius50" />
      <Placeholder width="40px" height="20px" borderRadius="radius50" />
      <TourOperatorLogoContainer className="hidden-on-xs">
        <Placeholder width="58px" height="22px" borderRadius="radius50" />
      </TourOperatorLogoContainer>
    </SkeletonHeader>

    <SkeletonBody $isHotelOnly={isHotelOnly}>
      <DetailsSection>
        <TitlePlaceholder>
          <Placeholder width="160px" height="18px" borderRadius="radius50" />
        </TitlePlaceholder>
        <ContentBox>
          <ServiceItem>
            <Placeholder width="20px" height="20px" borderRadius="radius50" />
            <Placeholder width="80%" height="16px" borderRadius="radius50" />
          </ServiceItem>
          <ServiceItem>
            <Placeholder width="20px" height="20px" borderRadius="radius50" />
            <Placeholder width="60%" height="16px" borderRadius="radius50" />
          </ServiceItem>
          <ServiceItem>
            <Placeholder width="20px" height="20px" borderRadius="radius50" />
            <Placeholder width="70%" height="16px" borderRadius="radius50" />
          </ServiceItem>
        </ContentBox>
      </DetailsSection>

      {!isHotelOnly && (
        <FlightsSection>
          <TitlePlaceholder>
            <Placeholder width="80px" height="18px" borderRadius="radius50" />
          </TitlePlaceholder>
          <ContentBox>
            <Placeholder width="140px" height="18px" borderRadius="radius50" />
            <FlightRow>
              <Placeholder width="100%" height="16px" borderRadius="radius50" />
              <Placeholder width="80%" height="14px" borderRadius="radius50" />
            </FlightRow>
            <Separator />
            <FlightRow>
              <Placeholder width="100%" height="16px" borderRadius="radius50" />
              <Placeholder width="80%" height="14px" borderRadius="radius50" />
            </FlightRow>
          </ContentBox>
        </FlightsSection>
      )}

      <PriceSection>
        <PriceRow>
          <PriceContainer>
            <Placeholder width="120px" height="32px" borderRadius="radius50" />
            <Placeholder width="80px" height="14px" borderRadius="radius50" />
          </PriceContainer>
          <Placeholder width="150px" height="40px" borderRadius="radius50" />
        </PriceRow>
      </PriceSection>
    </SkeletonBody>
  </SkeletonCard>
);

interface OfferListSkeletonProps {
  count?: number;
  isHotelOnly?: boolean;
}

const OfferListSkeleton: React.FC<OfferListSkeletonProps> = ({
  count = 10,
  isHotelOnly = false,
}) => {
  return (
    <Container>
      {[...Array(count)].map((_, index) => (
        <OfferListItemSkeleton key={index} isHotelOnly={isHotelOnly} />
      ))}
    </Container>
  );
};

export default OfferListSkeleton;
